import { store } from 'store';
import API from 'utils/api';
import { ESTADOS_GLOBALES_MAP } from 'utils/estadosGlobales';
import formatValues, { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';

export const baseURI = 'recaudacion/estatus-de-ejecuciones-pae/';

const estatusSorter = (a, b) => {
  // Si ambos tienen orden_de_uso 0, ordenar por id
  if (a.orden_de_uso === 0 && b.orden_de_uso === 0) {
    return a.id - b.id;
  }

  // Si solo a tiene orden_de_uso 0, enviarlo al final
  if (a.orden_de_uso === 0) {
    return 1;
  }

  // Si solo b tiene orden_de_uso 0, enviarlo al final
  if (b.orden_de_uso === 0) {
    return -1;
  }

  // Si ambos tienen orden_de_uso mayor a 0, ordenar normalmente
  if (a.orden_de_uso === b.orden_de_uso) {
    return a.id - b.id;
  }

  return a.orden_de_uso - b.orden_de_uso;
};

/**
 * Procesa los valores del formulario antes de enviarlos
 * @param {object} values - Valores del formulario
 * @returns {object} - Valores procesados
 */
const processFormValues = (values) => {
  const processedValues = { ...values };

  // Procesar vencimiento
  if (!processedValues?.tiene_vencimiento) {
    processedValues.numero_de_dias_de_vencimiento = 0;
    processedValues.vence_en_dias_habiles = false;
  }

  // Procesar reportes
  if (!processedValues?.imprime_reportes) {
    processedValues.reportes_id_a_imprimir = null;
  }

  // Procesar correo electrónico
  if (!processedValues?.envia_correo_electronico) {
    processedValues.titulo_de_correo = null;
    processedValues.contenido_de_correo = null;
  }

  // Procesar mensaje SMS
  if (!processedValues?.envia_mensaje_eletronico_sms) {
    processedValues.contenido_de_mensaje = null;
  }

  return formatValues(processedValues, { clean: false });
};

/**
 * GET Estatus de Ejecuciones
 * @param {object} params - Parámetros de consulta
 * @returns {Promise<object>}
 */
export const getEstatusDeEjecuciones = async (params) => {
  try {
    const _userRoles = store.getState().auth.roles.map((e) => e.id);
    const response = await API.get(baseURI, { params });

    return formatReceived(response.data.map((e) => ({
      ...e,
      tipo_de_cargo: e.tipos_de_cargos_estatus_de_ejecucion,
      tipo_de_gasto: e.tipos_de_gastos_estatus_de_ejecucion,
      puede_aplicar: e.roles_que_puedan_aplicar.some((r) => _userRoles.includes(r)),
    }))).sort(estatusSorter);
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Estatus de Ejecucion por ID
 * @param {number} id - ID del estatus de ejecución
 * @returns {Promise<object|null>}
 */
export const getEstatusDeEjecucion = async (id) => {
  try {
    const response = await API.get(`${baseURI}${id}`);
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * POST Estatus de Ejecucion
 * @param {object} values - Valores del formulario
 * @returns {Promise<object|boolean>}
 */
export const postEstatusDeEjecucion = async (values) => {
  try {
    const formatedValues = processFormValues(values);
    const response = await API.post(baseURI, formatedValues);
    onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Estatus de Ejecucion
 * @param {number} id - ID del estatus de ejecución
 * @param {object} values - Valores del formulario
 * @returns {Promise<object|boolean>}
 */
export const patchEstatusDeEjecucion = async (id, values) => {
  try {
    const formatedValues = processFormValues(values);
    const response = await API.patch(`${baseURI}${id}`, formatedValues);
    onSuccess(response, 'Editado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * DELETE Estatus de Ejecucion (cancelación lógica)
 * @param {number} id - ID del estatus de ejecución
 * @returns {Promise<boolean>}
 */
export const deleteEstatusDeEjecucion = async (id) => {
  try {
    const response = await API.patch(`${baseURI}${id}`, {
      estados_globales: ESTADOS_GLOBALES_MAP.CANCELADO,
    });
    onSuccess(response, 'Cancelado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

export const postSiguienteEstatusDeEjecucion = async (values) => {
  try {
    const formatedValues = formatValues(values, { clean: false });

    const response = await API.post('recaudacion/cambio-de-estados-masivo/cambiar-estados/', formatedValues);
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

const formatFileValues = (_values) => {
  if (_values.documento) {
    const values = { ..._values };
    const formData = new FormData();
    const valuesKeys = Object.keys(values);
    valuesKeys.forEach((key) => {
      formData.append(`${key}`, values[key] || '');
    });
    formData.append('tamanio', (values.documento.size / 1024 / 1024).toFixed(2));
    formData.append('extension_del_documento', values.documento.name.split('.').pop().toLowerCase());

    const _config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    return [formData, _config];
  }
  return [_values];
};

export const postArchivosDeEstadosDeEjecucion = async (_values) => {
  try {
    const [values, config] = formatFileValues(_values);
    const response = await API.post('recaudacion/archivos-de-ejecuciones-pae/', values, config);
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};
